import { useQuery } from '@tanstack/react-query';
import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '@/lib/api';
import { formatDate, formatLongDate, formatNumber } from '@/lib/format';
import { useDebounced, useMe, useTeam } from '@/lib/hooks';
import { toast, toastError } from '@/lib/toast';
import { Avatar, CompanyMono } from '@/ui/Avatar';
import { InputField, SearchInput } from '@/ui/Field';
import { Icon } from '@/ui/Icon';
import { Callout, Chip, Spinner, Switch } from '@/ui/bits';
import { MenuHead, MenuItem, MenuSep, Popover, usePopover, type PopoverState } from '@/ui/Popover';
import { SourceLinks, SourceMenuItems, uniqueSources } from '@/ui/Sources';
import { PERSON_EMAILS_MAX, SOURCE_KINDS } from '@shared/constants';
import { aiFieldWord, COMPANY_FIELD_LABELS, firstName, listWords, modelName, PERSON_FIELD_LABELS, type CompanySheet, type PersonSheet, type RecordSheet } from '@shared/record';
import type { AiFilledMark, SearchHit } from '@shared/types';
import { EditRow, fieldError, fromControl, useCommit, useFlash, useRecordPatch } from './edit';
import { bareUrl, first, hrefOf } from './util';

// The Details box (and, for a person, the Email preferences box) on the left of the record sheet.

const ref = (m: { id: string; name: string; hue: number } | null | undefined) => (m ? { id: m.id, name: m.name, hue: m.hue } : null);

function ExtLink({ value }: { value: string }) {
  return (
    <a className="link" href={hrefOf(value)} target="_blank" rel="noreferrer">
      {bareUrl(value)}
    </a>
  );
}

function wholeNumber(label: string, min: number, max: number) {
  return (next: string) => {
    if (!next) return null;
    const n = Number(next.replace(/[\s,.]/g, ''));
    if (!Number.isInteger(n)) return `Enter the ${label} as a whole number`;
    if (n < min || n > max) return `Enter a ${label} between ${formatNumber(min)} and ${formatNumber(max)}`;
    return null;
  };
}

/* ---------- a row that opens a picker ---------- */

export function PickRow({
  label,
  children,
  pop,
  readOnly,
  flashing,
  extra,
}: {
  label: string;
  children: ReactNode;
  pop: PopoverState;
  readOnly?: boolean;
  flashing: boolean;
  extra?: ReactNode;
}) {
  const valueId = useId();
  const cls = ['frow', readOnly ? '' : 'ed', flashing ? 'flash' : ''].filter(Boolean).join(' ');
  if (readOnly)
    return (
      <div className={cls}>
        <span className="fk">{label}</span>
        <span className="fv">{children}</span>
      </div>
    );
  return (
    <div
      className={cls}
      role="button"
      tabIndex={0}
      aria-label={`Edit ${label}`}
      aria-describedby={valueId}
      aria-haspopup="dialog"
      aria-expanded={pop.open}
      onClick={(e) => {
        if (!fromControl(e)) pop.openAt(e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          pop.openAt(e.currentTarget);
        }
      }}
    >
      <span className="fk">{label}</span>
      <span className="fv">
        <span id={valueId}>{children}</span>
        {extra}
      </span>
    </div>
  );
}

/* ---------- owner ---------- */

export function OwnerRow({ sheet, readOnly }: { sheet: RecordSheet; readOnly: boolean }) {
  const { active } = useTeam();
  const commit = useCommit(sheet);
  const pop = usePopover('dialog');
  const [flashing, flash] = useFlash();
  const pick = (id: string | null) => {
    if (id === (sheet.owner?.id ?? null)) return;
    const next = ref(active.find((m) => m.id === id));
    commit('Owner', { body: { ownerId: id }, show: { owner: next } }, { body: { ownerId: sheet.owner?.id ?? null }, show: { owner: sheet.owner } })
      .then(flash)
      .catch((err) => toastError(err));
  };
  return (
    <>
      <PickRow label="Owner" pop={pop} readOnly={readOnly} flashing={flashing}>
        {sheet.owner ? (
          <span className="owner-cell">
            <Avatar name={sheet.owner.name} hue={sheet.owner.hue} size="xs" />
            {sheet.owner.name}
          </span>
        ) : (
          <span className="fv-empty">Nobody yet</span>
        )}
      </PickRow>
      <Popover state={pop} width={240} align="left" label="Choose an owner">
        <MenuHead>Owner</MenuHead>
        {active.map((m) => (
          <MenuItem key={m.id} lead={<Avatar name={m.name} hue={m.hue} size="xs" />} checked={m.id === sheet.owner?.id} onSelect={() => pick(m.id)}>
            {m.name}
          </MenuItem>
        ))}
        <MenuSep />
        <MenuItem icon="x" checked={!sheet.owner} onSelect={() => pick(null)}>
          No owner
        </MenuItem>
      </Popover>
    </>
  );
}

/* ---------- company (on a person) ---------- */

function CompanyRow({ sheet, readOnly }: { sheet: PersonSheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const pop = usePopover('dialog');
  const [flashing, flash] = useFlash();
  const [q, setQ] = useState('');
  const query = useDebounced(q.trim(), 180);
  const hits = useQuery({
    queryKey: ['search', 'record-company', query],
    queryFn: ({ signal }) => api.get<{ companies: SearchHit[] }>('/search', { query: { q: query, limit: 8 }, signal }),
    enabled: pop.open && query.length > 0,
    staleTime: 30_000,
  });
  const pick = (hit: SearchHit | null) => {
    pop.close();
    setQ('');
    if ((hit?.id ?? null) === (sheet.company?.id ?? null)) return;
    const next = hit ? { id: hit.id, name: hit.title, hue: hit.hue, logo: hit.logo ?? null, industry: null, staff: null, location: null, domain: null, market: null, lacks: [] } : null;
    commit(
      'Company',
      { body: { companyId: hit?.id ?? null }, show: { company: next, mates: [] } },
      { body: { companyId: sheet.company?.id ?? null }, show: { company: sheet.company, mates: sheet.mates } },
    )
      .then(flash)
      .catch((err) => toastError(err));
  };
  const list = hits.data?.companies ?? [];
  return (
    <>
      <PickRow label="Company" pop={pop} readOnly={readOnly} flashing={flashing}>
        {sheet.company ? sheet.company.name : <span className="fv-empty">Add a company</span>}
      </PickRow>
      <Popover state={pop} width={280} align="left" role="dialog" label="Choose a company">
        <div className="pop-search">
          <SearchInput className="input-sm" label="Find a company" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a company" autoComplete="off" />
        </div>
        {query && hits.isFetching && !list.length ? (
          <div className="pop-note">
            <Spinner label="Searching" />
          </div>
        ) : null}
        {list.map((h) => (
          <MenuItem key={h.id} lead={<CompanyMono name={h.title} hue={h.hue} logo={h.logo} size="xs" />} checked={h.id === sheet.company?.id} onSelect={() => pick(h)}>
            <span className="mi-two">
              <span>{h.title}</span>
              {h.subtitle ? <small>{h.subtitle}</small> : null}
            </span>
          </MenuItem>
        ))}
        {query && !hits.isFetching && hits.data && !list.length ? <div className="pop-note">No company called “{query}”. Add it from Companies first.</div> : null}
        {!query ? <div className="pop-note">Type part of the name.</div> : null}
        {sheet.company ? (
          <>
            <MenuSep />
            <MenuItem icon="x" onSelect={() => pick(null)}>
              Not at {sheet.company.name} any more
            </MenuItem>
          </>
        ) : null}
      </Popover>
    </>
  );
}

/* ---------- source ---------- */

function SourceRow({ sheet, readOnly }: { sheet: RecordSheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const [editing, setEditing] = useState(false);
  const [source, setSource] = useState(sheet.source ?? '');
  const [detail, setDetail] = useState(sheet.sourceDetail ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [flashing, flash] = useFlash();
  const rowRef = useRef<HTMLDivElement>(null);
  const valueId = useId();
  const added = `Added${sheet.createdBy ? ` by ${first(sheet.createdBy)}` : ''} on ${formatLongDate(sheet.createdAt)}`;
  const options = [...new Set([...SOURCE_KINDS, ...(sheet.source ? [sheet.source] : [])])];

  const start = () => {
    setSource(sheet.source ?? '');
    setDetail(sheet.sourceDetail ?? '');
    setError(null);
    setEditing(true);
  };
  const stop = () => {
    setEditing(false);
    requestAnimationFrame(() => rowRef.current?.focus());
  };
  const save = async () => {
    const s = source.trim() || null;
    const d = detail.trim() || null;
    if (s === sheet.source && d === sheet.sourceDetail) {
      stop();
      return;
    }
    setBusy(true);
    try {
      await commit(
        'Source',
        { body: { source: s, sourceDetail: d }, show: { source: s, sourceDetail: d } },
        { body: { source: sheet.source, sourceDetail: sheet.sourceDetail }, show: { source: sheet.source, sourceDetail: sheet.sourceDetail } },
      );
      flash();
      stop();
    } catch (err) {
      setError(fieldError(err, 'source'));
    } finally {
      setBusy(false);
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      stop();
    } else if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      void save();
    }
  };

  if (editing)
    return (
      <div className="frow editing" ref={rowRef} onKeyDown={onKey}>
        <span className="fk">Source</span>
        <span className="fv src-edit">
          <select className="select select-sm" aria-label="Source" value={source} onChange={(e) => setSource(e.target.value)} autoFocus>
            <option value="">No source</option>
            {options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <input className="input input-sm" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Detail, such as the event or who referred them" aria-label="Source detail" />
          {error ? (
            <span className="field-err" role="alert">
              <Icon name="warn" className="i-xs" />
              {error}
            </span>
          ) : null}
          <span className="src-acts">
            <button type="button" className="btn btn-sm btn-ghost" onClick={stop}>
              Cancel
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={() => void save()} disabled={busy}>
              Save
            </button>
          </span>
        </span>
      </div>
    );
  const value = (
    <>
      {sheet.source ?? <span className="fv-empty">Add where they came from</span>}
      {sheet.sourceDetail ? <span className="sub">{sheet.sourceDetail}</span> : null}
      <span className="sub">{added}</span>
    </>
  );
  if (readOnly)
    return (
      <div className="frow">
        <span className="fk">Source</span>
        <span className="fv">{value}</span>
      </div>
    );
  return (
    <div
      className={['frow ed', flashing ? 'flash' : ''].join(' ')}
      ref={rowRef}
      role="button"
      tabIndex={0}
      aria-label="Edit Source"
      aria-describedby={valueId}
      onClick={start}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && e.key === 'Enter') {
          e.preventDefault();
          start();
        }
      }}
    >
      <span className="fk">Source</span>
      <span className="fv" id={valueId}>
        {value}
      </span>
    </div>
  );
}

/* ---------- a person's email addresses ---------- */

function EmailRows({ sheet, readOnly }: { sheet: PersonSheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const emails = sheet.emails.map((e) => e.email);
  const [main, ...others] = emails;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const errId = useId();
  const addRef = useRef<HTMLButtonElement>(null);

  const write = (label: string, next: string[]) =>
    commit(
      label,
      { body: { emails: next }, show: { emails: next.map((email, i) => ({ email, primary: i === 0 })) } },
      { body: { emails }, show: { emails: sheet.emails } },
    );

  const add = async () => {
    const next = draft.trim().toLowerCase();
    if (!next) {
      setAdding(false);
      return;
    }
    if (emails.includes(next)) {
      setError(`${next} is already listed.`);
      return;
    }
    setBusy(true);
    try {
      await write('Email addresses', [...emails, next]);
      setDraft('');
      setAdding(false);
      setError(null);
      requestAnimationFrame(() => addRef.current?.focus());
    } catch (err) {
      setError(fieldError(err, 'emails'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <EditRow
        label="Email"
        field="emails"
        kind="email"
        value={main ?? ''}
        empty="Add an email address"
        readOnly={readOnly}
        display={<span className="email-row">{main}</span>}
        save={(next) => write('Email', next ? [next.toLowerCase(), ...others.filter((o) => o !== next.toLowerCase())] : others)}
      />
      {others.length ? (
        <div className="frow">
          <span className="fk">Other emails</span>
          <span className="fv">
            {others.map((o) => (
              <span key={o} className="email-alt">
                <span className="email-row">{o}</span>
                {readOnly ? null : (
                  <span className="email-acts">
                    <button type="button" className="link" onClick={() => write('Main email', [o, ...emails.filter((e) => e !== o)]).catch((err) => toastError(err))}>
                      Make main
                    </button>
                    <button
                      type="button"
                      className="icon-btn xs"
                      aria-label={`Remove ${o}`}
                      data-tip="Remove"
                      onClick={() => write('Email addresses', emails.filter((e) => e !== o)).catch((err) => toastError(err))}
                    >
                      <Icon name="x" />
                    </button>
                  </span>
                )}
              </span>
            ))}
            <span className="sub">Listed, used for matching</span>
          </span>
        </div>
      ) : null}
      {readOnly || !main ? null : adding ? (
        <div className="frow editing">
          <span className="fk">Another email</span>
          <span className="fv">
            <input
              className="cell-input"
              type="email"
              value={draft}
              autoFocus
              disabled={busy}
              aria-label="Another email address"
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errId : undefined}
              onChange={(e) => {
                setDraft(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void add();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  setAdding(false);
                  setDraft('');
                  setError(null);
                  requestAnimationFrame(() => addRef.current?.focus());
                }
              }}
              onBlur={() => {
                if (!draft.trim()) setAdding(false);
              }}
            />
            {error ? (
              <span className="field-err" id={errId} role="alert">
                <Icon name="warn" className="i-xs" />
                {error}
              </span>
            ) : (
              <span className="sub">Enter to add, Esc to cancel</span>
            )}
          </span>
        </div>
      ) : emails.length >= PERSON_EMAILS_MAX ? (
        <span className="frow-add sub">Up to {PERSON_EMAILS_MAX} addresses per person</span>
      ) : (
        <button type="button" className="link frow-add" ref={addRef} onClick={() => setAdding(true)}>
          <Icon name="plus" className="i-xs" />
          Add another email
        </button>
      )}
    </>
  );
}

/* ---------- AI marks on a company or person ---------- */

/** "Source", "Sources", or "Researched from" when the AI read the pages for the whole company rather than this value. */
function sourcesHead(mark: Pick<AiFilledMark, 'sources' | 'broad'>): string {
  if (mark.broad) return 'Researched from';
  return (mark.sources?.length ?? 0) === 1 ? 'Source' : 'Sources';
}

export function AiBadge({ sheet, field, onEdit }: { sheet: RecordSheet; field: string; onEdit?: () => void }) {
  const mark = sheet.aiFilled[field];
  const patch = useRecordPatch(sheet);
  const pop = usePopover('menu');
  if (!mark) return null;
  const label = (sheet.type === 'person' ? PERSON_FIELD_LABELS : COMPANY_FIELD_LABELS)[field] ?? field;
  const sources = mark.sources ?? [];
  const confirm = () => {
    patch({ confirmAi: [field] }, { aiFilled: { ...sheet.aiFilled, [field]: { ...mark, reviewed: true } } })
      .then(() => toast(`${label} marked as checked`, { icon: 'check' }))
      .catch((err) => toastError(err));
  };
  const sourceList = sources.length ? (
    <>
      <MenuSep />
      <MenuHead>{sourcesHead(mark)}</MenuHead>
      <SourceMenuItems sources={sources} />
    </>
  ) : null;
  if (mark.reviewed && !sources.length)
    return (
      <Chip tone="success" icon="check" title={`Filled by ${modelName(mark.model)} on ${formatDate(mark.at)}, then checked`}>
        Checked
      </Chip>
    );
  if (mark.reviewed)
    return (
      <>
        <button type="button" className="chip chip-success" aria-label={`${label} was filled by AI and checked, see where it came from`} {...pop.anchorProps}>
          <Icon name="check" />
          Checked
        </button>
        <Popover state={pop} width={280} align="left" label={`${label} filled by AI`}>
          <MenuHead>{label} · checked</MenuHead>
          <p className="pop-note">
            Filled by {modelName(mark.model)} on {formatDate(mark.at)}, then checked.
          </p>
          {sourceList}
          {onEdit ? (
            <>
              <MenuSep />
              <MenuItem icon="edit" onSelect={onEdit}>
                Edit value
              </MenuItem>
            </>
          ) : null}
        </Popover>
      </>
    );
  return (
    <>
      <button type="button" className="chip chip-info" aria-label={`${label} was filled by AI, review it`} {...pop.anchorProps}>
        <Icon name="sparkle" />
        Filled by AI
      </button>
      <Popover state={pop} width={280} align="left" label={`${label} filled by AI`} initialFocus=".ai-confirm">
        <MenuHead>{label} · filled by AI</MenuHead>
        <p className="pop-note">
          {modelName(mark.model)} on {formatDate(mark.at)}.
        </p>
        <MenuItem icon="check" onSelect={confirm} className="ai-confirm">
          Looks right, mark checked
        </MenuItem>
        {onEdit ? (
          <MenuItem icon="edit" onSelect={onEdit}>
            Edit value
          </MenuItem>
        ) : null}
        {sourceList}
      </Popover>
    </>
  );
}

const NUMBER_WORDS = ['no', 'one', 'both', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];

/** The "Filled by AI" callout with one button to confirm every value at once. */
export function AiReview({ sheet }: { sheet: RecordSheet }) {
  const me = useMe();
  const patch = useRecordPatch(sheet);
  const [done, setDone] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const open = Object.entries(sheet.aiFilled).filter(([, m]) => !m.reviewed);
  if (done !== null)
    return (
      <Callout tone="success" title="AI fields reviewed" role="status">
        {done === 1 ? 'The value was' : done === 2 ? 'Both values were' : `All ${NUMBER_WORDS[done] ?? formatNumber(done)} values were`} checked by{' '}
        {firstName(me.user.name)} today.
      </Callout>
    );
  if (!open.length) return null;
  const latest = open.map(([, m]) => m).sort((a, b) => b.at.localeCompare(a.at))[0]!;
  const models = [...new Set(open.map(([, m]) => modelName(m.model)))];
  const sources = uniqueSources(open.flatMap(([, m]) => m.sources ?? []));
  const broad = open.every(([, m]) => !m.sources?.length || m.broad);
  const words = listWords(open.map(([f]) => aiFieldWord(sheet.type, f)));
  const confirm = async () => {
    setBusy(true);
    try {
      const fields = open.map(([f]) => f);
      await patch(
        { confirmAi: fields },
        { aiFilled: Object.fromEntries(Object.entries(sheet.aiFilled).map(([f, m]) => [f, { ...m, reviewed: true }])) },
      );
      setDone(fields.length);
      toast(`${fields.length === 1 ? 'The AI-filled field' : `${fields.length} AI-filled fields`} marked as checked`, { icon: 'check' });
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Callout
      tone="info"
      icon="sparkle"
      title={`Filled by AI on ${formatDate(latest.at)}`}
      actions={
        <button type="button" className="btn btn-sm btn-tile" onClick={() => void confirm()} disabled={busy}>
          <Icon name="check" />
          Looks right
        </button>
      }
    >
      {listWords(models)} filled {words}
      {sources.length ? (
        <>
          {broad ? ' after reading ' : ' from '}
          <SourceLinks sources={sources} />
        </>
      ) : null}
      . Check {open.length === 1 ? 'it' : 'them'} once and {open.length === 1 ? 'it is' : 'they are'} marked as reviewed.
    </Callout>
  );
}

/** Where the AI Finder found a person, under their source. */
function FoundRow({ sheet }: { sheet: PersonSheet }) {
  const found = sheet.aiFound;
  if (!found?.sources.length) return null;
  return (
    <div className="frow">
      <span className="fk">{found.broad ? 'Researched from' : 'Found on'}</span>
      <span className="fv">
        <SourceLinks sources={found.sources} />
        <span className="sub">
          Found by {modelName(found.model)} on {formatLongDate(found.at)}
        </span>
      </span>
    </div>
  );
}

/* ---------- the boxes ---------- */

/** "Edit value" from a badge's menu opens the row for typing. */
function useEditRow() {
  const box = useRef<HTMLDivElement>(null);
  const edit = (field: string) => () => {
    const row = box.current?.querySelector<HTMLElement>(`[data-field="${field}"] .frow.ed, .frow.ed[data-field="${field}"]`);
    row?.click();
  };
  return { box, edit };
}

export function PersonDetails({ sheet, readOnly }: { sheet: PersonSheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const { box, edit } = useEditRow();
  const text = (field: 'title' | 'phone' | 'linkedin' | 'location', label: string) => (next: string) =>
    commit(label, { body: { [field]: next || null }, show: { [field]: next || null } }, { body: { [field]: sheet[field] }, show: { [field]: sheet[field] } });
  const badge = (field: string) => (readOnly ? null : <AiBadge sheet={sheet} field={field} onEdit={edit(field)} />);
  return (
    <section className="tile" aria-labelledby={`det-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`det-${sheet.id}`}>Details</h3>
      </div>
      <div className="fields" ref={box}>
        <EmailRows sheet={sheet} readOnly={readOnly} />
        <div data-field="phone">
          <EditRow label="Phone" field="phone" kind="tel" value={sheet.phone ?? ''} empty="Add a phone number" readOnly={readOnly} save={text('phone', 'Phone')} extra={badge('phone')} />
        </div>
        <div data-field="linkedin">
          <EditRow
            label="LinkedIn"
            field="linkedin"
            kind="url"
            value={sheet.linkedin ?? ''}
            display={sheet.linkedin ? <ExtLink value={sheet.linkedin} /> : undefined}
            empty="Add a LinkedIn profile"
            readOnly={readOnly}
            save={text('linkedin', 'LinkedIn')}
            extra={badge('linkedin')}
          />
        </div>
        <div data-field="title">
          <EditRow label="Title" field="title" value={sheet.title ?? ''} empty="Add a job title" readOnly={readOnly} save={text('title', 'Title')} extra={badge('title')} />
        </div>
        <CompanyRow sheet={sheet} readOnly={readOnly} />
        <div data-field="location">
          <EditRow label="Location" field="location" value={sheet.location ?? ''} empty="Add a location" readOnly={readOnly} save={text('location', 'Location')} extra={badge('location')} />
        </div>
        <OwnerRow sheet={sheet} readOnly={readOnly} />
        <SourceRow sheet={sheet} readOnly={readOnly} />
        <FoundRow sheet={sheet} />
      </div>
    </section>
  );
}

type CompanyText = 'domain' | 'website' | 'industry' | 'location' | 'linkedin' | 'phone' | 'description';

export function CompanyDetails({ sheet, readOnly }: { sheet: CompanySheet; readOnly: boolean }) {
  const commit = useCommit(sheet);
  const { box, edit } = useEditRow();
  const text = (field: CompanyText, label: string) => (next: string) =>
    commit(label, { body: { [field]: next || null }, show: { [field]: next || null } }, { body: { [field]: sheet[field] }, show: { [field]: sheet[field] } });
  const num = (field: 'staff' | 'founded', label: string) => (next: string) => {
    const n = next ? Number(next.replace(/[\s,.]/g, '')) : null;
    return commit(label, { body: { [field]: n }, show: { [field]: n } }, { body: { [field]: sheet[field] }, show: { [field]: sheet[field] } });
  };
  const badge = (field: string) => (readOnly ? null : <AiBadge sheet={sheet} field={field} onEdit={edit(field)} />);
  const thisYear = new Date().getFullYear();
  return (
    <section className="tile" aria-labelledby={`det-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`det-${sheet.id}`}>Details</h3>
      </div>
      <div className="fields" ref={box}>
        <div data-field="domain">
          <EditRow label="Domain" field="domain" value={sheet.domain ?? ''} empty="Add a domain" readOnly={readOnly} save={text('domain', 'Domain')} extra={badge('domain')} />
        </div>
        <div data-field="website">
          <EditRow
            label="Website"
            field="website"
            kind="url"
            value={sheet.website ?? ''}
            display={sheet.website ? <ExtLink value={sheet.website} /> : undefined}
            empty="Add a website"
            readOnly={readOnly}
            save={text('website', 'Website')}
            extra={badge('website')}
          />
        </div>
        <div data-field="industry">
          <EditRow label="Industry" field="industry" value={sheet.industry ?? ''} empty="Add an industry" readOnly={readOnly} save={text('industry', 'Industry')} extra={badge('industry')} />
        </div>
        <div data-field="staff">
          <EditRow
            label="Staff"
            field="staff"
            kind="number"
            value={sheet.staff === null ? '' : String(sheet.staff)}
            display={sheet.staff === null ? undefined : formatNumber(sheet.staff)}
            empty="Add a staff count"
            validate={wholeNumber('staff count', 0, 10_000_000)}
            readOnly={readOnly}
            save={num('staff', 'Staff count')}
            extra={badge('staff')}
          />
        </div>
        <div data-field="founded">
          <EditRow
            label="Founded"
            field="founded"
            kind="number"
            value={sheet.founded === null ? '' : String(sheet.founded)}
            empty="Add the year it was founded"
            validate={wholeNumber('year', 1600, thisYear)}
            readOnly={readOnly}
            save={num('founded', 'Founded')}
            extra={badge('founded')}
          />
        </div>
        <div data-field="linkedin">
          <EditRow
            label="LinkedIn"
            field="linkedin"
            kind="url"
            value={sheet.linkedin ?? ''}
            display={sheet.linkedin ? <ExtLink value={sheet.linkedin} /> : undefined}
            empty="Add a LinkedIn page"
            readOnly={readOnly}
            save={text('linkedin', 'LinkedIn')}
            extra={badge('linkedin')}
          />
        </div>
        <div data-field="phone">
          <EditRow label="Phone" field="phone" kind="tel" value={sheet.phone ?? ''} empty="Add a phone number" readOnly={readOnly} save={text('phone', 'Phone')} extra={badge('phone')} />
        </div>
        <div data-field="location">
          <EditRow label="Location" field="location" value={sheet.location ?? ''} empty="Add a location" readOnly={readOnly} save={text('location', 'Location')} extra={badge('location')} />
        </div>
        <div data-field="description">
          <EditRow
            label="Description"
            field="description"
            kind="long"
            value={sheet.description ?? ''}
            display={<span className="pre">{sheet.description}</span>}
            empty="Add a short description"
            readOnly={readOnly}
            save={text('description', 'Description')}
            extra={badge('description')}
          />
        </div>
        <OwnerRow sheet={sheet} readOnly={readOnly} />
        <SourceRow sheet={sheet} readOnly={readOnly} />
      </div>
    </section>
  );
}

/* ---------- email preferences ---------- */

export function EmailPrefs({ sheet, readOnly }: { sheet: PersonSheet; readOnly: boolean }) {
  const patch = useRecordPatch(sheet);
  const [reason, setReason] = useState(sheet.doNotEmailReason ?? '');
  const [error, setError] = useState<string | null>(null);
  const [seen, setSeen] = useState(sheet.doNotEmailReason);
  if (seen !== sheet.doNotEmailReason) {
    setSeen(sheet.doNotEmailReason);
    setReason(sheet.doNotEmailReason ?? '');
  }

  const toggle = (on: boolean) => {
    const before = { doNotEmail: sheet.doNotEmail, doNotEmailReason: sheet.doNotEmailReason };
    patch({ doNotEmail: on }, { doNotEmail: on })
      .then(() =>
        toast(on ? `${sheet.name} will not get list emails` : `${sheet.name} can be emailed again`, {
          icon: on ? 'lock' : 'mail',
          undo: () => {
            patch(before, before).catch((err) => toastError(err));
          },
        }),
      )
      .catch((err) => toastError(err));
  };
  const saveReason = () => {
    const next = reason.trim() || null;
    if (next === sheet.doNotEmailReason) return;
    patch({ doNotEmailReason: next }, { doNotEmailReason: next })
      .then(() => {
        setError(null);
        toast('Reason saved', { icon: 'check-circle' });
      })
      .catch((err) => setError(fieldError(err, 'doNotEmailReason')));
  };

  return (
    <section className="tile" aria-labelledby={`prefs-${sheet.id}`}>
      <div className="tile-h">
        <h3 id={`prefs-${sheet.id}`}>Email preferences</h3>
      </div>
      <div className="dne-toggle" style={{ marginTop: 0 }}>
        <div className="grow">
          <b id={`dne-${sheet.id}`}>Do not email</b>
          <span>
            {sheet.doNotEmail
              ? (sheet.doNotEmailReason ?? 'Kept out of list sends, and the composer warns before a one-to-one email')
              : 'Excluded from list sends and the composer warns before sending'}
          </span>
        </div>
        <Switch checked={sheet.doNotEmail} onChange={toggle} label="Do not email" disabled={readOnly} />
      </div>
      {sheet.doNotEmail && !readOnly ? (
        <InputField
          label="Reason"
          className="dne-reason"
          inputClassName="input-sm"
          value={reason}
          placeholder="Such as “Asked to be left off newsletters”"
          error={error}
          onChange={(e) => setReason(e.target.value)}
          onBlur={saveReason}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              saveReason();
            } else if (e.key === 'Escape' && reason !== (sheet.doNotEmailReason ?? '')) {
              e.preventDefault();
              setReason(sheet.doNotEmailReason ?? '');
            }
          }}
        />
      ) : null}
      {sheet.unsubscribedAt ? (
        <Callout tone="plain" className="dne-unsub">
          Unsubscribed from list emails on {formatDate(sheet.unsubscribedAt)}. One-to-one emails still go through.
        </Callout>
      ) : null}
    </section>
  );
}
